import type { ReelGems } from '@goodtime/shared';
import type { SpinOutcome } from '../../widgets/slot/types';

/** A gem cell on the reels and the gems it pays. */
export interface GemCell {
  col: number;
  row: number;
  gems: number;
}

/** The gem cells the server paid on a spin, what it paid for them, and the
 *  spin's `wallet.gems`, which already has them. */
export interface PaidReelGems {
  cells: GemCell[];
  gems: number;
  walletGems: number;
}

/** A landed spin's gem cells to light, and who pays their gems. */
export interface GemCellsMoment {
  cells: GemCell[];
  /** What the server paid for its cells; null when it paid none. */
  paid: PaidReelGems | null;
  /** The gems of the app's own cells (none on the server's reels). */
  own: number;
}

/**
 * A spin's gem cells, split by who pays them. The server pays the ones it
 * lists in the spin's `reelGems` (`reel_gems` on the rr_v1 reels), and they
 * are already in the spin's wallet. Any other gem cell on the grid is one the
 * server does not pay (legacy maths, the multiplayer reels, a local spin), so
 * the app counts it as it always has.
 */
export interface SpinGems {
  /** Cells the server paid, with the server's values. */
  paid: GemCell[];
  /** What the server says they paid in all. */
  paidGems: number;
  /** Gem cells on the grid the server did not list, with the theme's values. */
  unpaid: GemCell[];
  unpaidGems: number;
}

/**
 * Read a spin's gem cells. A cell the server listed is the server's,
 * whatever symbol the grid shows there; the rest of the grid's gem cells
 * (by their {@link SlotSymbolDef#gemValue}) are the app's to count.
 */
export function spinGems(outcome: SpinOutcome, reelGems: ReelGems | null | undefined): SpinGems {
  const paid: GemCell[] = (reelGems?.cells ?? [])
    .filter((c) => c.gems > 0)
    .map((c) => ({ col: c.col, row: c.row, gems: c.gems }));
  const listed = new Set(paid.map((c) => `${c.col}:${c.row}`));
  const unpaid: GemCell[] = [];
  outcome.grid.forEach((reel, col) => {
    reel.forEach((symbol, row) => {
      const gems = symbol.gemValue ?? 0;
      if (gems > 0 && !listed.has(`${col}:${row}`)) unpaid.push({ col, row, gems });
    });
  });
  return {
    paid,
    paidGems: reelGems && reelGems.gems > 0 ? reelGems.gems : 0,
    unpaid,
    unpaidGems: unpaid.reduce((n, c) => n + c.gems, 0),
  };
}

/**
 * How many of the gems the server paid on a spin the player's gem count
 * should take now: the server's figure, but never past the spin's wallet. A
 * wallet sync that ran while the reels were landing has brought those gems
 * in already, and they must not be counted twice.
 *
 * @param paidGems   the spin's `reelGems.gems`
 * @param walletGems the spin's `wallet.gems`, which already has them
 * @param localGems  the gems the app shows now
 */
export function reelGemsCredit(paidGems: number, walletGems: number, localGems: number): number {
  if (paidGems <= 0) return 0;
  return Math.max(0, Math.min(paidGems, walletGems - localGems));
}

/**
 * The gem cells' moment for a spin that has landed, or null when no gem cell
 * did: every gem cell to light, the server's (`paid`) and the app's own
 * (`settlement.gemPositions`, with their `gemValue`; none on the server's
 * reels).
 */
export function gemCellsMoment(
  outcome: SpinOutcome,
  settlement: { gemsAwarded: number; gemPositions: readonly { col: number; row: number }[] },
  paid: PaidReelGems | null | undefined,
): GemCellsMoment | null {
  const own = settlement.gemPositions.map((pos) => ({
    col: pos.col,
    row: pos.row,
    gems: outcome.grid[pos.col]?.[pos.row]?.gemValue ?? 0,
  }));
  const cells = [...(paid?.cells ?? []), ...own].filter((c) => c.gems > 0);
  if (cells.length === 0) return null;
  return { cells, paid: paid ?? null, own: Math.max(0, settlement.gemsAwarded) };
}

/**
 * The gems a moment counts onto the player's gems when its gems reach the
 * wallet: the server's figure for its cells, through {@link reelGemsCredit}
 * (never past the spin's wallet), and the app's own cells' values.
 *
 * @param localGems the gems the app shows at that moment
 */
export function gemsToCount(moment: GemCellsMoment, localGems: number): number {
  const fromServer = moment.paid ? reelGemsCredit(moment.paid.gems, moment.paid.walletGems, localGems) : 0;
  return fromServer + moment.own;
}
